import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { applicationActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import BackSpace from '@material-ui/icons/Backspace';
import Tooltip from '@material-ui/core/Tooltip';
import { ApplicationApi, FileApi, ListApi } from '@apply/api';
import { ConfirmModal } from '@apply/ui';
import { applicationInsights } from '../../../ApplicationInsights';
import { InformModal, FileSelectDialog, BfiNumber } from '../../../common/SharedComponents';
import { RefreshEvents } from '../../../common/SharedEvents';
import { LoadPanel } from 'devextreme-react/load-panel';
import Tabs, { Item } from 'devextreme-react/tabs';
import { ApplyFileStatusType } from '../../common/ApplicationConstants';
import { ModalSummaryHeader } from '../../common/ApplicationComponents';
import { handleAdminSuccess, handleAdminError, handleAdminWarning } from '../../../common/SharedFunctions';
import { ApplicationStatusType, FileGenerationStatusType } from '@apply/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRepeat, faDownload, faGears } from '@fortawesome/free-solid-svg-icons';
import { FileList } from '../../common/components/FileList';

const reviewStyles = {
    buttonMarginRight: { marginRight: '10px' },
    hrDefault: { marginTop: '0' },
    nonEditable: {
        backgroundColor: '#f7f7f7',
        padding: '5px 10px',
        margin: '5px',
    },
    noPaddingRight: { paddingLeft: '0' },
    noPaddingLeft: { paddingLeft: '0' },
    margin10Bottom: { marginBottom: '10px' },
    leftLabel: { paddingLeft: '0', paddingTop: '8px', paddingRight: '0' },
    iconContainer: { paddingTop: '8px', paddingLeft: '0', paddingRight: '0' },
    paidContainer: { paddingTop: '8px', marginBottom: '1rem' },
    statusMsgContainer: {
        paddingTop: '8px',
        paddingLeft: '10px',
        paddingRight: '0',
    },
    statusMsgContainer2: {
        paddingTop: '20px',
        paddingLeft: '10px',
        paddingRight: '0',
    },
    iconYes: { color: 'mediumblue', cursor: 'pointer' },
    iconNo: { color: 'silver', cursor: 'not-allowed' },
    iconCancel: { color: 'darkred', cursor: 'pointer' },
    buttonContainer: { marginRight: '20px', textAlign: 'right' },
    blue: { color: 'blue' },
    green: { color: 'green' },
    red: { color: 'red' },
    black: { color: 'black' },
    fileRow: { height: '45px' },
};

const ApplicationReviewModal = () => {
    const { isOpen, id, propertyId = 0 } = useSelector((state) => state.application.applicationReviewModal);
    const { dispatch } = store;
    const refreshEvents = new RefreshEvents();
    const applicationApi = new ApplicationApi.ApplicationApi(applicationInsights);
    const fileApi = new FileApi.FileApi(applicationInsights);
    const listApi = new ListApi.ListApi(applicationInsights);

    const [tabIndex, set_tabIndex] = useState(-1);

    const [application, set_application] = useState(null);
    const [properties, set_properties] = useState([]);
    const [property, set_property] = useState(null);
    const [responsibleId, set_responsibleId] = useState(0);
    const [bfiNumber, set_bfiNumber] = useState('');
    const [isPaid, set_isPaid] = useState(false);
    const [appStatusId, set_appStatusId] = useState(ApplicationStatusType.Pending);
    const [canEditPaid, set_canEditPaid] = useState(true);
    const [employees, set_employees] = useState([]);
    const [applicationFiles, set_applicationFiles] = useState([]);
    const [confirmNumbersText, set_confirmNumbersText] = useState('');
    const [confirmAppraiseText, set_confirmAppraiseText] = useState('');
    const [openConfirmCancelModal, set_openConfirmCancelModal] = useState(false);
    const [canAppraise, set_canAppraise] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');
    const [isDirty, set_isDirty] = useState(false);
    const [openInformModal, set_openInformModal] = useState(false);
    const [informModalMessage, set_informModalMessage] = useState('');

    useEffect(() => {
        const loadListsAsync = async () => {
            await _loadBfiEmployees();
        };
        loadListsAsync();
    }, []);

    useEffect(() => {
        if (!id) {
            resetState();
            return;
        }

        loadApplication(id);
    }, [id]);

    useEffect(() => {
        if (!properties) {
            set_tabIndex(-1);
            return;
        }

        if (!(tabIndex >= 0)) {
            const firstActiveIndex = properties.findIndex((p) => !p.disabled);
            set_tabIndex(firstActiveIndex);
        }
    }, [properties]);

    useEffect(() => {
        if (!properties || properties.length === 0) {
            set_property(null);
            return;
        }
        if (tabIndex < 0) {
            return;
        }
        set_property(properties[tabIndex]);
    }, [tabIndex]);

    const onResponsibleIdChanged = (value) => {
        set_responsibleId(value);
        set_isDirty(true);
    };

    const onBfiNumberChanged = (value) => {
        set_bfiNumber(value);
        set_isDirty(true);
    };

    const onIsPaidChanged = (value) => {
        set_isPaid(value);
        set_isDirty(true);
    };

    const resetState = () => {
        set_tabIndex(-1);
        set_application(null);
        set_properties([]);
        set_property(null);
        set_responsibleId('');
        set_bfiNumber(null);
        set_isPaid(false);
        set_appStatusId(ApplicationStatusType.Pending);
        set_canEditPaid(true);
        set_applicationFiles([]);
        set_confirmNumbersText('');
        set_confirmAppraiseText('');
        set_openConfirmCancelModal(false);
        set_canAppraise(false);
    };

    const fileStatuses = [
        { id: ApplyFileStatusType.No, name: 'Not Provided' },
        { id: ApplyFileStatusType.Pending, name: 'Pending' },
        { id: ApplyFileStatusType.Approved, name: 'Approved' },
        { id: ApplyFileStatusType.NotApplicable, name: 'Upload/Replace' },
        { id: ApplyFileStatusType.Rejected, name: 'Rejected' },
    ];

    const copyFileStatuses = (fileExists) => {
        const output = [];
        fileStatuses.forEach((s) => {
            if (s.id !== 0 || !fileExists) {
                var status = { ...s };
                if (status.id === -1) {
                    status.name = fileExists ? 'Replace' : 'Upload';
                }
                output.push(status);
            }
        });
        return output;
    };

    const toBoolean = (string) => {
        switch (string.toLowerCase().trim()) {
            case 'true':
            case 'yes':
            case '1':
                return true;
            case 'false':
            case 'no':
            case '0':
            case null:
                return false;
            default:
                return Boolean(string);
        }
    };

    const appStatuses = [
        { id: ApplicationStatusType.Pending, name: 'Pending' },
        { id: ApplicationStatusType.Appraise, name: 'Appraise' },
    ];

    const _handleClose = () => {
        if (isDirty) {
            set_openConfirmCancelModal(true);
            return;
        }
        dispatch(
            applicationActions.setApplicationReviewModal({
                applicationReviewModal: { isOpen: false },
            })
        );
    };

    const _handelCancelConfirm = async () => {
        set_openConfirmCancelModal(false);
        dispatch(
            applicationActions.setApplicationReviewModal({
                applicationReviewModal: { isOpen: false },
            })
        );
    };

    const _handelCancelCancel = () => {
        set_openConfirmCancelModal(false);
    };

    const _loadBfiEmployees = async () => {
        const data = await listApi.getBfiEmployees();
        set_employees(data);
    };

    const onApplicationFileListChange = (file) => {
        const targetFile = applicationFiles.find((f) => f.id === file.id);
        if (!targetFile) {
            return;
        }

        set_isDirty(true);
        const newFileList = applicationFiles.map((u) => (u.id !== file.id ? u : file));
        set_applicationFiles(newFileList);

        const canAppraise = newFileList.find((f) => f.fileTypeId === 0)?.statusId === ApplyFileStatusType.Approved;
        set_canAppraise(canAppraise);

        if (!canAppraise) {
            properties.forEach((p) => {
                if (p.statusId === ApplyFileStatusType.Pending) {
                    p.statusId = 0;
                }
            });
        }
    };

    const onPropertyFileListChange = (file, parentId) => {
        var property = properties.find((p) => p.id === parentId);
        if (!property) {
            return;
        }
        const targetFile = property.files.find((f) => f.id === file.id);
        if (!targetFile) {
            return;
        }

        set_isDirty(true);
        property.files = property.files.map((u) => (u.id !== file.id ? u : file));
        var newProperties = properties.map((p) => (p.id !== property.id ? p : property));
        set_properties(newProperties);
    };

    const allFilesApproved = (files) => {
        if (!files || files.length === 0) {
            return true;
        }
        return files.every((a) => a.statusId === ApplyFileStatusType.Approved);
    };

    const _validateSubmission = async (step, event) => {
        if (event) {
            event.preventDefault();
        }

        step = step ?? 1;

        if (step === 1) {
            if (appStatusId === ApplicationStatusType.Appraise) {
                //Ensure that all properties files are in correct state
                const propsToAppraise = [];
                const activeProperties = properties;
                activeProperties.forEach((p) => {
                    if (
                        !allFilesApproved(applicationFiles.filter((f) => f.requiresApproval === true)) ||
                        !allFilesApproved(p.files.filter((f) => f.requiresApproval === true))
                    ) {
                        propsToAppraise.push(p);
                    }
                });

                if (propsToAppraise.length > 1) {
                    const names = propsToAppraise.reduce(function (a, b) {
                        return (a.bfiNumberSuffix || a) + ', ' + b.bfiNumberSuffix;
                    });
                    set_confirmAppraiseText(
                        `Not all required files were approved for properties ${names}. Are you sure you want to move this Application to Appraise?`
                    );
                    return;
                } else if (propsToAppraise.length === 1) {
                    const name = propsToAppraise[0].bfiNumberSuffix;
                    if (!name) {
                        set_confirmAppraiseText(
                            `Not all required files were approved for the property. Are you sure you want to move this property to Appraise?`
                        );
                    } else {
                        set_confirmAppraiseText(
                            `Not all files were approved for property ${name}. Are you sure you want to move this property to Appraise?`
                        );
                    }
                    return;
                }
            }
            step = 2;
        }

        //Check Loan and BfiNumbers to ensure they are unique
        if (step === 2) {
            if (bfiNumber) {
                const model = {
                    id: application.id,
                    bfiNumber,
                    isMasterNote: false,
                };
                var result = (await applicationApi.checkLoanAndBfiNumber(model)).result;
                let msg = result.message;
                if (result.isValid) {
                    if (msg) {
                        msg += ' Do you wish to continue?';
                        set_confirmNumbersText(msg);
                        return;
                    }
                } else {
                    set_informModalMessage(msg);
                    set_openInformModal(true);
                    return;
                }
            }
            step = 3;
        }

        if (step === 3) {
            await _submitChanges();
        }
    };

    const getModel = () => {
        return {
            id: application.id,
            statusId: appStatusId,
            bfiNumber,
            responsibleId,
            isPaid: isPaid,
            files: [],
            properties: [],
        };
    };

    const _submitChanges = async () => {
        try {
            set_loadingMessage('Begin Save...');

            //Assemble the outgoing model
            const model = getModel();

            set_loadingMessage(`Uploading new application files...`);
            await Promise.all(
                applicationFiles.map(async (f) => {
                    if (f.file) {
                        const upLoadResult = await fileApi.uploadTempFiles([f.file]);
                        let result = upLoadResult.result;
                        if (result && result.length > 0) {
                            f.fileName = result[0].cleanedName;
                            f.tempFileName = result[0].tempName;
                            f.file = null;
                        }
                    }
                    const fileOut = {
                        id: f.id,
                        statusId: f.statusId,
                        tempFileName: f.tempFileName,
                    };
                    model.files.push(fileOut);
                })
            );

            set_loadingMessage('Uploading new property files...');

            await Promise.all(
                properties
                    .filter((p) => !p.disabled)
                    .map(async (p) => {
                        const propOut = {
                            id: p.id,
                            files: [],
                            statusId: p.statusId,
                        };
                        model.properties.push(propOut);

                        await Promise.all(
                            p.files.map(async (f) => {
                                if (f.file) {
                                    const upLoadResult = await fileApi.uploadTempFiles([f.file]);
                                    let result = upLoadResult.result;
                                    if (result && result.length > 0) {
                                        f.fileName = result[0].cleanedName;
                                        f.tempFileName = result[0].tempName;
                                        f.file = null;
                                    }
                                }

                                const fileOut = {
                                    id: f.id,
                                    statusId: f.statusId,
                                    tempFileName: f.tempFileName,
                                };
                                propOut.files.push(fileOut);
                            })
                        );
                    })
            );

            set_loadingMessage('Saving changes (please be patient)...');
            const response = await applicationApi.updatePropertyReview(model);
            if (response.statusCode !== 200) {
                return;
            }

            refreshEvents.dispatchRefreshEvent();
            handleAdminSuccess('Changes saved successfully!');

            setTimeout(() => {
                dispatch(
                    applicationActions.setApplicationReviewModal({
                        applicationReviewModal: { isOpen: false },
                    })
                );
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error occurred. Please try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    const _handelNumbersConfirm = async () => {
        set_confirmNumbersText('');
        await _validateSubmission(3);
    };

    const _handelNumbersCancel = () => {
        set_confirmNumbersText('');
    };

    const _handelAppraiseConfirm = async () => {
        set_confirmAppraiseText('');
        await _validateSubmission(2);
    };

    const _handelAppraiseCancel = () => {
        set_confirmAppraiseText('');
    };

    const displayFileRegenerateStatus = (genStatusId) => {
        switch (genStatusId) {
            case FileGenerationStatusType.NotApplicable:
                handleAdminError(
                    new Error('The file is not of a type to regenerate!'),
                    'The file is not of a type to regenerate!'
                );
                break;
            case FileGenerationStatusType.GenerationOk:
                handleAdminSuccess('The file was regenerated successully.');
                break;
            case FileGenerationStatusType.ToBeGenerated:
                handleAdminSuccess('The file has not yet been regenerated.');
                break;
        }
    };

    const regenerateApplicationFile = async (fileId) => {
        try {
            set_loadingMessage('Submitting PDF regeneration request...');
            const response = await applicationApi.regenerateApplicationFile(fileId);
            if (response.statusCode !== 200) {
                throw new Error(`Application File regeneration request return statuscode=${response.statusCode}`);
            } else {
                const file = application.files.find((a) => a.id === fileId);
                if (file) file.generationStatusId = FileGenerationStatusType.ToBeGenerated;
                handleAdminSuccess('File regeneration queued successfully. Please check back/refresh in a few minutes!');
            }
        } catch (ex) {
            handleAdminError(ex, 'PDF file regeneration request failed! Please try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    const updateApplicationFileStatus = async (fileId) => {
        try {
            set_loadingMessage('Requesting PDF Status...');
            const response = await applicationApi.requestApplicationFile(fileId);
            if (response.statusCode !== 200) {
                throw new Error(`Property File Status request returned statuscode=${response.statusCode}`);
            }

            const file = application.files.find((a) => a.id === fileId);
            if (file) {
                const fileOut = response.result;
                file.generationStatusId = fileOut.generationStatusId;
                file.url = fileOut.url;
            }
        } catch (ex) {
            handleAdminError(ex, 'PDF file status request failed! Please try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    const regeneratePropertyFile = async (fileId) => {
        try {
            set_loadingMessage('Submitting PDF regeneration request...');
            const response = await applicationApi.regeneratePropertyFile(fileId);
            if (response.statusCode !== 200) {
                throw new Error(`Property File regeneration request return statuscode=${response.statusCode}`);
            } else {
                const file = property.files.find((a) => a.id === fileId);
                if (file) file.generationStatusId = FileGenerationStatusType.ToBeGenerated;
                handleAdminSuccess('File regeneration queued successfully. Please check back/refresh in a few minutes!');
            }
        } catch (ex) {
            handleAdminError(ex, 'PDF file regeneration request failed! Please try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    const updatePropertyFileStatus = async (fileId) => {
        try {
            set_loadingMessage('Requesting PDF Status...');
            const response = await applicationApi.requestPropertyFileStatus(fileId);
            if (response.statusCode !== 200) {
                throw new Error(`Property File Status request returned statuscode=${response.statusCode}`);
            }

            const file = property.files.find((a) => a.id === fileId);
            if (file) {
                const fileOut = response.result;
                file.generationStatusId = fileOut.generationStatusId;
                file.url = fileOut.url;
                displayFileRegenerateStatus(file.generationStatusId);
            }
        } catch (ex) {
            handleAdminError(ex, 'PDF file status request failed! Please try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    const _handelInformModalConfirm = () => {
        set_openInformModal(false);
    };

    const loadApplication = async (id) => {
        try {
            set_loadingMessage('Loading Application...');

            const review = (await applicationApi.getSubmittedAppReview(id)).result;
            if (!review) {
                return;
            }

            set_application(review);
            set_appStatusId(review.statusId);
            //Set editable items
            set_responsibleId(review.responsibleId);
            set_bfiNumber(review.bfiNumber);

            review.files.forEach((file) => {
                if (file.fileTypeId !== 1) {
                    //No status options for Termsheet
                    file.statuses = copyFileStatuses(!!file.url);
                    if (file.fileTypeId === 0) {
                        //L1
                        set_canAppraise(file.statusId === ApplyFileStatusType.Approved);
                    }
                } else {
                    file.createdDate = review.createdDate;
                }
            });

            review.properties.forEach((p) => {
                if (propertyId && p.id !== propertyId) {
                    p.disabled = true;
                }
                p.files.forEach((file) => {
                    file.statuses = copyFileStatuses(!!file.url);
                });
            });

            set_properties(review.properties);
            set_property(properties[tabIndex]);
            set_applicationFiles(review.files);
            set_isPaid(review.isPaid);
            set_canEditPaid(!review.isPaid);
        } catch (ex) {
            handleAdminError(ex, 'Unexpected Error loading application! Please try again.');
        } finally {
            set_isDirty(false);
            set_loadingMessage('');
        }
    };

    const paidStatuses = [
        { key: false, name: 'No' },
        { key: true, name: 'Yes' },
    ];

    const onTabsSelectionChanged = (args) => {
        if (args.name === 'selectedIndex') {
            let ti = args.value;
            if (args.value >= 0) {
                var prop = properties[ti];
                if (prop.disabled) {
                    ti = properties.findIndex((p) => !p.disabled);
                }
            }
            set_tabIndex(ti);
        }
    };

    const allArizonaProperties = () => {
        return properties.every((p) => {
            return p?.address.state === 'AZ';
        });
    };

    if (!application) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex='-1'
                            role='dialog'
                            style={{ width: '800px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <div>
                                    <div
                                        className='page-header'
                                        role='banner'
                                    >
                                        <h1>Submitted Application Review</h1>
                                        <button
                                            type='button'
                                            className='close'
                                            data-dismiss='modal'
                                            aria-label='Close'
                                            onClick={_handleClose}
                                        >
                                            <span aria-hidden='true'>&times;</span>
                                        </button>
                                    </div>
                                    <ModalSummaryHeader application={application} />
                                    <form
                                        name='submittedAppReviewForm'
                                        onSubmit={(event) => _validateSubmission(1, event)}
                                    >
                                        <div
                                            className='scrollable-content'
                                            style={{ margin: '10px' }}
                                        >
                                            <div>
                                                <h2 className='decorated'>Setup</h2>
                                                <div className='row'>
                                                    <div
                                                        className='columns small-12 large-4'
                                                        style={reviewStyles.noPaddingLeft}
                                                    >
                                                        <label htmlFor='responsibleId'>POC</label>
                                                        <select
                                                            id='responsibleId'
                                                            value={responsibleId}
                                                            onChange={(event) => onResponsibleIdChanged(event.target.value)}
                                                        >
                                                            <option
                                                                key=''
                                                                value=''
                                                            ></option>
                                                            {employees &&
                                                                employees.map((poc) => {
                                                                    return (
                                                                        <option
                                                                            key={poc.key}
                                                                            value={poc.key}
                                                                        >
                                                                            {poc.name}
                                                                        </option>
                                                                    );
                                                                })}
                                                        </select>
                                                    </div>
                                                    <div className='columns small-12 large-4'>
                                                        <label htmlFor='bfiNumber'>BFI Number</label>
                                                        <BfiNumber
                                                            id={'bfiNumber'}
                                                            onChange={onBfiNumberChanged}
                                                            value={bfiNumber}
                                                            required={false}
                                                        />
                                                    </div>
                                                </div>
                                                <h2 className='decorated'>Loan Artifacts</h2>
                                                {application?.termsheetFile && (
                                                    <div className='row'>
                                                        <div
                                                            className='columns small-12 large-6'
                                                            style={reviewStyles.leftLabel}
                                                        >
                                                            Termsheet
                                                        </div>
                                                        <div
                                                            className='columns small-12 large-1'
                                                            style={reviewStyles.iconContainer}
                                                        >
                                                            <a href={application.termsheetFile.url}>
                                                                <FontAwesomeIcon
                                                                    icon={faDownload}
                                                                    style={reviewStyles.iconYes}
                                                                />
                                                            </a>
                                                        </div>
                                                    </div>
                                                )}
                                                <FileList
                                                    files={applicationFiles}
                                                    onFileChange={onApplicationFileListChange}
                                                    isApplicationLevel={true}
                                                    onFileRegenerate={regenerateApplicationFile}
                                                    onFileStatusUpdate={updateApplicationFileStatus}
                                                />
                                                <div className='row'>
                                                    <div
                                                        className='columns small-12 large-2'
                                                        style={reviewStyles.leftLabel}
                                                    >
                                                        <label htmlFor='isPaid'>Paid</label>
                                                    </div>
                                                    {allArizonaProperties() ? (
                                                        <div
                                                            className='columns small-12 large-4'
                                                            style={reviewStyles.paidContainer}
                                                        >
                                                            N/A <em>(Arizona property)</em>
                                                        </div>
                                                    ) : (
                                                        <div className='columns small-12 large-4'>
                                                            <select
                                                                id='isPaid'
                                                                value={isPaid ?? false}
                                                                onChange={(event) =>
                                                                    onIsPaidChanged(toBoolean(event.target.value))
                                                                }
                                                                required
                                                                style={
                                                                    isPaid || isPaid === 'true'
                                                                        ? reviewStyles.green
                                                                        : reviewStyles.red
                                                                }
                                                                disabled={!canEditPaid}
                                                            >
                                                                {paidStatuses &&
                                                                    paidStatuses.map((elm) => {
                                                                        return (
                                                                            <option
                                                                                key={elm.key}
                                                                                value={elm.key}
                                                                                style={reviewStyles.black}
                                                                            >
                                                                                {elm.name}
                                                                            </option>
                                                                        );
                                                                    })}
                                                            </select>
                                                        </div>
                                                    )}
                                                </div>

                                                <h2 className='decorated'>
                                                    {application.isPortfolio ? 'Properties' : 'Property'}
                                                </h2>
                                                <div>
                                                    {application.isPortfolio && (
                                                        <Tabs
                                                            showNavButtons={true}
                                                            width={'80%'}
                                                            selectedIndex={tabIndex}
                                                            onOptionChanged={onTabsSelectionChanged}
                                                        >
                                                            {application.properties.map((p) => {
                                                                return (
                                                                    <Item
                                                                        key={'prop' + p.id}
                                                                        text={'Prop ' + p.bfiNumberSuffix}
                                                                        disabled={p.disabled}
                                                                    />
                                                                );
                                                            })}
                                                        </Tabs>
                                                    )}
                                                    {property && (
                                                        <div
                                                            style={{
                                                                border: '1px solid silver',
                                                                padding: '5px',
                                                                marginBottom: '10px',
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    minHeight: '300px',
                                                                }}
                                                            >
                                                                <div className='row'>
                                                                    <div
                                                                        className='columns small-12 large-12'
                                                                        style={reviewStyles.nonEditable}
                                                                    >
                                                                        {property.description && (
                                                                            <div>
                                                                                <b>
                                                                                    Property {property.bfiNumberSuffix} -{' '}
                                                                                    {property.description}
                                                                                </b>
                                                                            </div>
                                                                        )}
                                                                        {property.address && (
                                                                            <div
                                                                                style={{
                                                                                    marginTop: '5px',
                                                                                    marginBottom: '10px',
                                                                                    fontWeight: 'bold',
                                                                                }}
                                                                            >
                                                                                {property.address?.street1}{' '}
                                                                                {property.address?.street2}{' '}
                                                                                {property.address?.city},{' '}
                                                                                {property.address?.state}{' '}
                                                                                {property.address?.zipCode}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <h2 className='decorated'>Property Artifacts</h2>
                                                                <FileList
                                                                    parentId={property.id}
                                                                    files={property.files}
                                                                    onFileChange={onPropertyFileListChange}
                                                                    disabled={property.disabled}
                                                                    onFileRegenerate={regeneratePropertyFile}
                                                                    onFileStatusUpdate={updatePropertyFileStatus}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}

                                                    <h2 className='decorated'>Status</h2>
                                                    <div className='row'>
                                                        <div
                                                            className='columns small-12 large-3'
                                                            style={reviewStyles.leftLabel}
                                                        >
                                                            <label htmlFor='statusId'>Application Status</label>
                                                        </div>
                                                        <div className='columns small-12 large-4'>
                                                            <select
                                                                id='statusId'
                                                                value={appStatusId}
                                                                onChange={(event) =>
                                                                    set_appStatusId(parseInt(event.target.value))
                                                                }
                                                                required
                                                            >
                                                                {appStatuses.map((fs) => {
                                                                    return (
                                                                        <option
                                                                            key={fs.id}
                                                                            value={fs.id}
                                                                            disabled={
                                                                                fs.id === ApplicationStatusType.Appraise &&
                                                                                !canAppraise
                                                                            }
                                                                        >
                                                                            {fs.name}
                                                                        </option>
                                                                    );
                                                                })}
                                                            </select>
                                                        </div>
                                                        <div
                                                            className='columns small-12 large-1'
                                                            style={reviewStyles.iconContainer}
                                                        ></div>
                                                        <div
                                                            className='columns small-12 large-4'
                                                            style={reviewStyles.statusMsgContainer}
                                                        >
                                                            {application?.statusChangedByMsg && application.statusChangedByMsg}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            className='button-footer'
                                            style={reviewStyles.buttonContainer}
                                        >
                                            <button
                                                style={reviewStyles.buttonMarginRight}
                                                type='submit'
                                                className='button primary'
                                                title='Save changes and close modal'
                                            >
                                                Save
                                            </button>
                                            <button
                                                type='button'
                                                className='button hollow'
                                                data-dismiss='modal'
                                                onClick={_handleClose}
                                                title='Abandon changes and close modal'
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <ConfirmModal
                isOpen={!!confirmNumbersText}
                text={confirmNumbersText}
                confirmCallback={_handelNumbersConfirm}
                cancelCallback={_handelNumbersCancel}
            />
            <ConfirmModal
                isOpen={!!confirmAppraiseText}
                text={confirmAppraiseText}
                confirmCallback={_handelAppraiseConfirm}
                cancelCallback={_handelAppraiseCancel}
            />
            <ConfirmModal
                isOpen={openConfirmCancelModal}
                text={'Are you sure you want to Cancel and lose changes?'}
                confirmCallback={_handelCancelConfirm}
                cancelCallback={_handelCancelCancel}
            />
            <InformModal
                isOpen={openInformModal}
                text={informModalMessage}
                confirmCallback={_handelInformModalConfirm}
            />
            <LoadPanel
                hideOnOutsideClick={false}
                visible={!!loadingMessage}
                message={loadingMessage}
            />
        </>
    );
};

export default ApplicationReviewModal;
